import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { CourseType, CourseMode, CourseStatus } from '@masamune/shared';

export function NewCourseModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: '', code: '', type: CourseType.FORMATEMP as string, mode: CourseMode.BLENDED as string,
    status: CourseStatus.ISCRIZIONI as string,
    partnerName: '', clientId: '',
    startDate: '', endDate: '', hoursTotal: 80,
    enrolledCount: 0, revenue: 0, cost: 0,
    description: '', objectives: '',
    teacherUserIds: [] as string[],
  });

  const { data: clients } = useQuery({
    queryKey: ['clients-lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; legalName: string }> }>('/clients?pageSize=200'),
  });
  const { data: users } = useQuery({
    queryKey: ['users-lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; firstName: string; lastName: string; initials: string }> }>('/users?pageSize=200'),
  });

  const create = useMutation({
    mutationFn: () => api.post('/courses', {
      ...form,
      clientId: form.clientId || null,
      code: form.code || null,
      description: form.description || null,
      objectives: form.objectives || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['courses'] });
      toast.ok('Corso creato', form.name);
      onClose();
    },
    onError: (err) => toast.alert('Errore creazione corso', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const toggleTeacher = (id: string) => setForm({
    ...form,
    teacherUserIds: form.teacherUserIds.includes(id)
      ? form.teacherUserIds.filter((x) => x !== id)
      : [...form.teacherUserIds, id],
  });

  const isFondimpresa = form.type === CourseType.FONDIMPRESA;

  return (
    <Modal
      title="Nuovo corso"
      kicker="Talent · Programmazione"
      onClose={onClose}
      size="xl"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()}
            disabled={!form.name || !form.startDate || !form.endDate || (isFondimpresa && !form.clientId) || create.isPending}>
            Crea corso <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-section">
        <div className="form-section-hd">Identità</div>
        <div className="form-grid">
          <Field label="Nome corso" required span={8}>
            <input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Codice corso" span={4}>
            <input className="inp mono uppercase" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </Field>
          <Field label="Tipo finanziamento" span={6}>
            <div className="seg-control">
              {[CourseType.FORMATEMP, CourseType.FONDIMPRESA, CourseType.INTERNO].map((t) => (
                <button key={t} aria-pressed={form.type === t} onClick={() => setForm({ ...form, type: t })}>{t}</button>
              ))}
            </div>
          </Field>
          <Field label="Modalità" span={6}>
            <div className="seg-control">
              {[CourseMode.BLENDED, CourseMode.REMOTO, CourseMode.IN_PRESENZA, CourseMode.IN_HOUSE].map((m) => (
                <button key={m} aria-pressed={form.mode === m} onClick={() => setForm({ ...form, mode: m })}>{m.replace('_', ' ')}</button>
              ))}
            </div>
          </Field>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Stakeholder</div>
        <div className="form-grid">
          <Field label="Partner / Agenzia" hint={isFondimpresa ? 'Es. Fondimpresa' : 'Es. Agenzia Temp S.p.A.'} span={6}>
            <input className="inp" value={form.partnerName} onChange={(e) => setForm({ ...form, partnerName: e.target.value })} />
          </Field>
          <Field label={isFondimpresa ? 'Azienda cliente' : 'Cliente'} required={isFondimpresa} span={6}>
            <select className="inp" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}>
              <option value="">— Nessuno —</option>
              {(clients?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.legalName}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Periodo & numeri</div>
        <div className="form-grid">
          <Field label="Data inizio" required span={3}>
            <input className="inp mono" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          </Field>
          <Field label="Data fine" required span={3}>
            <input className="inp mono" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
          </Field>
          <Field label="Ore totali" span={3}>
            <input className="inp mono text-right" type="number" value={form.hoursTotal} onChange={(e) => setForm({ ...form, hoursTotal: Number(e.target.value) })} />
          </Field>
          <Field label="Iscritti previsti" span={3}>
            <input className="inp mono text-right" type="number" value={form.enrolledCount} onChange={(e) => setForm({ ...form, enrolledCount: Number(e.target.value) })} />
          </Field>
          <Field label="Ricavo (€)" span={6}>
            <input className="inp mono text-right" type="number" value={form.revenue} onChange={(e) => setForm({ ...form, revenue: Number(e.target.value) })} />
          </Field>
          <Field label="Costo (€)" span={6}>
            <input className="inp mono text-right" type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: Number(e.target.value) })} />
          </Field>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Docenti interni</div>
        <div className="grid grid-cols-3 gap-1.5 max-h-[160px] overflow-y-auto">
          {(users?.items ?? []).map((u) => {
            const selected = form.teacherUserIds.includes(u.id);
            return (
              <div key={u.id}
                onClick={() => toggleTeacher(u.id)}
                className="flex items-center gap-2 p-2 border rounded-2 cursor-pointer hover:bg-paper-2"
                style={{ borderColor: selected ? 'var(--accent)' : 'var(--line)', background: selected ? 'var(--paper-2)' : 'var(--surface)' }}>
                <span className="inline-grid place-items-center w-6 h-6 rounded-full bg-paper-2 text-[12px] font-semibold">{u.initials}</span>
                <span className="text-[13px] flex-1 truncate">{u.firstName} {u.lastName}</span>
                {selected && <Icon name="check" size={10} />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Descrizione & obiettivi</div>
        <div className="form-grid">
          <Field label="Descrizione breve" span={12}>
            <textarea className="inp" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Field label="Obiettivi formativi" span={12}>
            <textarea className="inp" rows={3} value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
